import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ConfigService } from '@ng-darwin/config';
import {
  AppConfigInterface,
  EndPointsInterface,
} from '../models/app-config.interface';
import { EntityInterface } from '../models/entity.interface';
import { map } from 'rxjs/operators';
import { EntityProductType } from '../models/entity.definitions';
import { Constants } from '@app/models/constants.class';
import { lastValueFrom } from 'rxjs';
import { TealiumService } from './tealium.service';

/**
 * @description Injectable
 * @class EntityService
 */
@Injectable({
  providedIn: 'root',
})
export class EntityService {
  private readonly _appConfig: AppConfigInterface = {};
  private readonly _endPoints: EndPointsInterface = {};
  private _selectedEntity: EntityInterface = {};
  private _selectedEntities: EntityInterface[] = [];
  private _categorizerEntity: EntityInterface = {};
  private _selectedEntityProductType: EntityProductType | null = null;
  private _baseURL = '';

  /**
   * @description Creates an instance of EntityService.
   * @param _http HttpClient
   * @param _configService ConfigService
   * @param _tealiumService TealiumService
   */
  constructor(
    private readonly _http: HttpClient,
    private readonly _configService: ConfigService,
    private readonly _tealiumService: TealiumService
  ) {
    this._appConfig = this._configService.config.app || {};
    this._endPoints = this._appConfig.endPoints || {};
    this._baseURL = (
      sessionStorage.getItem('app-unique-id') ===
      this._configService.config.app['cdncApplication']
        ? this._appConfig.baseUrlCDNC
        : this._appConfig.baseUrl
    ) as string;
    const selectedEntityFromSessionStorage =
      sessionStorage.getItem('selectedEntity');
    const selectedEntitiesFromSessionStorage =
      sessionStorage.getItem('selectedEntities');
    const parsedEntities: [] = JSON.parse(selectedEntitiesFromSessionStorage!);
    if (selectedEntityFromSessionStorage) {
      const parsedEntity = JSON.parse(selectedEntityFromSessionStorage);
      this.setSelectedEntity(parsedEntity);
      this.setSelectedEntityProductType(EntityProductType.psd2);
    }
    if (parsedEntities instanceof Array) {
      parsedEntities.forEach((entity) => {
        this.setSelectedEntities(entity);
        this.setSelectedEntityProductType(EntityProductType.psd2);
      });
    }
  }

  /**
   * @description setSelectedEntity
   * @param value EntityInterface
   */
  setSelectedEntity(value: EntityInterface): void {
    sessionStorage.setItem('selectedEntity', JSON.stringify(value));
    this._selectedEntity = value;
    this._tealiumService.setAddedBank(value.name as string);
  }

  setSelectedEntities(value: EntityInterface): void {
    const entityFound = this._selectedEntities.find(
      (entity: EntityInterface) => entity.code === value.code
    );
    entityFound
      ? (this._selectedEntities = this._selectedEntities.filter(
        (entity: EntityInterface) => entity.code !== value.code
      ))
      : this._selectedEntities.push(value);
    sessionStorage.setItem(
      'selectedEntities',
      JSON.stringify(this._selectedEntities)
    );
    this._tealiumService.setAddedBank(value.name as string);
  }

  /**
   * @description getSelectedEntity
   * @returns EntityInterface
   */
  getSelectedEntity(): EntityInterface {
    return this._selectedEntity;
  }

  getSelectedEntities(): EntityInterface[] {
    return this._selectedEntities;
  }

  /**
   * @description removeSelectedEntities
   * @readony
   */
  removeSelectedEntities(): void {
    sessionStorage.removeItem('selectedEntities');
    this._selectedEntities = [];
  }

  /**
   * @description setCategorizerEntity
   * @param value EntityInterface
   */
  setCategorizerEntity(value: EntityInterface): void {
    this._categorizerEntity = value;
  }

  /**
   * @description getCategorizerEntity
   * @returns EntityInterface
   */
  getCategorizerEntity(): EntityInterface {
    return this._categorizerEntity;
  }

  /**
   * @description getSelectedEntityProductType
   * @returns (EntityProductType | null)
   */
  getSelectedEntityProductType(): EntityProductType | null {
    return this._selectedEntityProductType;
  }

  /**
   * @description setSelectedEntityProductType
   * @param value EntityProductType
   */
  setSelectedEntityProductType(value: EntityProductType): void {
    this._selectedEntityProductType = value;
  }

  /**
   * @description getEntities
   * @returns Promise<EntityInterface[]>
   */
  getEntities(): Promise<EntityInterface[]> {
    const url = `${this._baseURL}/${this._endPoints.entities}`;
    return lastValueFrom(
      this._http.get<EntityInterface>(url).pipe(
        map((values) => {
          let entities: EntityInterface[] = [];
          const dataMap = new Map(Object.entries(values));
          if (dataMap.size > 0) {
            dataMap.forEach((data: EntityInterface[]) => {
              entities = data.filter(
                (entity) => entity.code !== 'PRV72400000049'
              );
              entities.forEach((entity) => {
                const testName = (entity.name || '').replace(/\s/g, '');
                const matchedBank = Constants.banksWithImages.find(b => b.toLowerCase() === testName.toLowerCase());
                entity.image = matchedBank
                  ? `assets/banks/${matchedBank}.svg`
                  : undefined;
              });
            });
          }
          return entities;
        })
      )
    );
  }
}
